<script setup lang="ts">
import { onBeforeMount, ref } from 'vue'
import { selectNotification } from '@/stores/notifications/utils'
import { useNetworkStore } from '@/stores/network/store'
import { useNotificationStore } from '@/stores/notifications/store'
import AppButton from '@/components/Base/Buttons/AppButton.vue'
import BaseIcon from '@/components/Base/BaseIcon.vue'
import ModalBackdrop from '@/components/Base/Modal/ModalBackdrop.vue'
import Spinner from '@/components/Base/Spinner.vue'

const networkStore = useNetworkStore()
const notificationStore = useNotificationStore()

withDefaults(
  defineProps<{
    isLoading?: boolean
    dpaConsentMissing?: boolean
  }>(),
  {
    isLoading: false,
  }
)

const emit = defineEmits<{
  (e: 'secondaryButtonClick', event: MouseEvent): void
  (e: 'primaryButtonClick', event: MouseEvent): void
}>()

const dpaIsLoading = ref(true)

type DpaMessage = {
  html: string
}

const dpaMessage = ref<DpaMessage>({
  html: '',
})

onBeforeMount(async () => {
  try {
    const response = await networkStore.loadAnalyticsDPA()

    // Clean the HTML before saving it
    dpaMessage.value = {
      html: sanitizeWordHtml(response.html),
    }
  } catch (error) {
    notificationStore.add(selectNotification(error))
  } finally {
    dpaIsLoading.value = false
  }
})

function sanitizeWordHtml(html: string): string {
  if (!html) return ''

  return (
    html
      // 1. Remove Word/Outlook comments (<!--[if ...]> and similar)
      .replace(/<!--[\s\S]*?-->/gi, '')

      // 2. Remove <style>...</style> blocks
      .replace(/<style[\s\S]*?<\/style>/gi, '')

      // 3. Clean up 'style' attributes, keeping only 'text-align'
      .replace(/style=['"]([^'"]*)['"]/gi, (match, styleContent) => {
        const textAlign = styleContent
          .split(';')
          .find((s: string) => s.toLowerCase().includes('text-align'))

        return textAlign ? `style="${textAlign.trim()}"` : ''
      })

      // 4. Remove font families and sizes (residual or outside 'style')
      .replace(/font-family:[^;"]*;?/gi, '')
      .replace(/font-size:[^;"]*;?/gi, '')

      // 5. Remove all Word classes (MsoNormal, etc.)
      .replace(/class=['"][^'"]*['"]/gi, '')
  )
}
</script>

<template>
  <ModalBackdrop>
    <div
      class="modal flex justify-between static z-20 bg-neutral-0 w-full md:w-[656px] md:rounded-lg modal__info"
    >
      <BaseIcon
        size="3rem"
        name="InfoCircle"
        class="self-center mb-5 modal__info--info-icon"
      />

      <!-- Title -->
      <span
        data-test="modal-title"
        class="self-center text-neutral-800 font-medium text-lg mb-2"
      >
        <slot name="title">
          {{
            dpaConsentMissing
              ? $t('settings.analytics.dpa.title')
              : $t('settings.analytics.enable.title')
          }}
        </slot>
      </span>

      <div v-if="dpaIsLoading" class="p-2 m-2 rounded flex items-center">
        <Spinner class="flex-1" />
      </div>

      <div v-if="isLoading" class="absolute block right-1/2 top-1/2">
        <Spinner class="mt-[-24px] mr-[-24px]" />
      </div>

      <div v-if="!dpaIsLoading" class="pt-4">
        <div :class="isLoading ? 'opacity-25' : ''">
          <div class="mb-4 text-sm text-neutral-700 text-center">
            {{ $t('settings.analytics.datatransfer') }}
          </div>
          <!-- {{ dpaMessage }} -->
          <div class="my-8 h-72 overflow-auto rounded-md bg-neutral-50">
            <p
              class="dpa-container p-2 scroll-shadows"
              v-html="dpaMessage.html"
            ></p>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <AppButton
            class="text-neutral-600 text-sm !bg-neutral-0 border-neutral-300 border"
            data-test="modal-secondary-button"
            @click="emit('secondaryButtonClick', $event)"
            :disabled="isLoading"
          >
            <slot name="secondaryButton">
              {{ $t('global.action.cancel') }}
            </slot>
          </AppButton>

          <AppButton
            class="primary text-sm"
            data-test="modal-primary-button"
            @click="emit('primaryButtonClick', $event)"
            :disabled="isLoading"
          >
            <slot name="primaryButton">
              {{ $t('global.action.confirm') }}
            </slot>
          </AppButton>
        </div>
      </div>
    </div>
  </ModalBackdrop>
</template>

<style scoped>
@reference "#tailwind";

.dpa-container * {
  font-size: 12px;
}

.scroll-shadows {
  max-height: 18rem;
  overflow: auto;

  background:
    /* Shadow Cover TOP */
    linear-gradient(white 30%, rgba(255, 255, 255, 0)) center top,
    /* Shadow Cover BOTTOM */ linear-gradient(rgba(255, 255, 255, 0), white 70%)
      center bottom,
    /* Shadow TOP */
      radial-gradient(
        farthest-side at 50% 0,
        rgba(0, 0, 0, 0.2),
        rgba(0, 0, 0, 0)
      )
      center top,
    /* Shadow BOTTOM */
      radial-gradient(
        farthest-side at 50% 100%,
        rgba(0, 0, 0, 0.2),
        rgba(0, 0, 0, 0)
      )
      center bottom;

  background-repeat: no-repeat;
  background-size:
    100% 40px,
    100% 40px,
    100% 14px,
    100% 14px;
  background-attachment: local, local, scroll, scroll;
}

.modal {
  @apply shadow-md;
}

.modal__info {
  @apply flex flex-col flex-nowrap w-full h-full md:h-auto md:max-w-[634px] p-6;
}

.modal__info--success-icon {
  @apply text-success-100;
}

.modal__info--error-icon {
  @apply text-danger-100;
}

.modal__info--loading-icon {
  @apply text-neutral-0 animate-spin;
}

.modal__info--info-icon {
  fill: none;
}

.modal__info--warning-icon {
  fill: none;
}

.modal__form {
  @apply flex flex-col sm:min-w-[375px] md:max-w-[750px] md:max-h-[378px];
}
</style>
